import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ArrowLeft, 
  ArrowRight, 
  Check, 
  X, 
  Upload,
  Loader2,
  FileText,
  Trash2,
  User,
  Building2,
  Briefcase,
  MapPin,
  DollarSign,
  Target,
  Activity,
  File,
  Share2,
  Copy,
  Linkedin,
  HelpCircle,
  BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Taxonomy data - Alphabetically sorted, "Other" last
const INDUSTRIES = [
  "Academia",
  "Biotechnology - Platform/Tools",
  "Biotechnology - Therapeutics",
  "CDMO/CMO",
  "Consulting/Agencies",
  "CRO",
  "Diagnostics",
  "Digital Health",
  "Health Data/RWE",
  "Hospitals/Health Systems",
  "Medical Devices",
  "Payers/PBMs",
  "Pharmaceutical (Big Pharma)",
  "Pharmaceutical (Mid/Specialty)",
  "Other"
];

const FUNCTIONS = [
  "BD/Corp Dev",
  "Clinical Development",
  "Commercial Operations",
  "Data Science/AI",
  "Finance",
  "HEOR/RWE",
  "HR",
  "Legal",
  "Market Access/Pricing",
  "Marketing",
  "Medical Affairs",
  "Patient Services",
  "R&D/Discovery",
  "Regulatory Affairs",
  "Safety/PV",
  "Sales",
  "Strategy",
  "Technical Operations/Manufacturing",
  "Other"
];

const COMPANY_SIZES = ["<50", "50-249", "250-999", "1,000-10,000", "10,000+"];
const COMPANY_STAGES = ["No Preference", "Preclinical", "Phase I/II", "Phase III", "Commercial-stage"];
const OWNERSHIP_TYPES = ["Private (VC-backed)", "Private (Bootstrapped)", "Public", "Nonprofit/Academic"];
const WORK_MODELS = ["Remote", "Hybrid", "On-site"];
const GEOGRAPHIES = ["US - East", "US - West", "US - Midwest", "US - South", "Europe", "UK", "Canada", "Asia", "Remote Only"];
const SALARY_BANDS = ["$80K - $120K", "$120K - $160K", "$160K - $200K", "$200K - $250K", "$250K - $300K", "$300K+"];
const EQUITY_PREFERENCES = ["Prefer equity", "Prefer cash", "Neutral"];
const DISEASE_AREAS = ["Oncology", "Rare Disease", "CNS", "Cardiometabolic", "Immunology", "Infectious Disease", "Respiratory", "GI", "Other"];
const EXPERIENCE_LEVELS = ["0-2 years", "3-5 years", "6-10 years", "11-20 years", "20+ years"];
const DISEASE_EXP_LEVELS = ["None", "Interest only", "Direct experience", "Commercialized", "Clinical research"];

// Section definitions with icons
const SECTIONS = [
  { id: 'about_you', name: 'About You', steps: 4, icon: User },
  { id: 'industry_function', name: 'Industries & Functions', steps: 2, icon: Building2 },
  { id: 'company_preferences', name: 'Company Preferences', steps: 3, icon: Briefcase },
  { id: 'work_style', name: 'Work Style', steps: 2, icon: MapPin },
  { id: 'compensation', name: 'Compensation', steps: 2, icon: DollarSign },
  { id: 'dealbreakers', name: 'Deal Makers & Breakers', steps: 2, icon: Target },
  { id: 'disease_areas', name: 'Disease Areas', steps: 2, icon: Activity },
  { id: 'resume', name: 'Resume', steps: 1, icon: File },
];

// Section Help Content
const SECTION_HELP = {
  about_you: {
    title: 'About You',
    purpose: 'This section captures your current professional identity — your role, seniority, and career context.',
    why: 'Understanding where you are today helps us match you with roles that represent logical next steps, not random opportunities.',
    tips: [
      'Be specific about your current title and years of experience',
      'Your seniority level affects the types of roles we show you',
      'Active/passive status helps us prioritize urgency'
    ]
  },
  industry_function: {
    title: 'Industries & Functions',
    purpose: 'Define which life sciences sectors and job functions interest you most.',
    why: 'This is the foundation of your matching. We only show roles in industries and functions you\'ve selected.',
    tips: [
      'Select "No Preference" if you\'re truly open to anything',
      'More selections = more potential matches',
      '"Other" options let you specify unlisted preferences'
    ]
  },
  company_preferences: {
    title: 'Company Preferences',
    purpose: 'Tell us what kind of company environment you thrive in.',
    why: 'Company stage and size dramatically affect day-to-day work. A Series A startup feels very different from Big Pharma.',
    tips: [
      'Consider where you\'ve been happiest professionally',
      'These preferences filter your matches significantly',
      'Be honest — there\'s no "right" answer'
    ]
  },
  work_style: {
    title: 'Work Style',
    purpose: 'Specify your location and remote work preferences.',
    why: 'Work arrangement is often a dealbreaker. We ensure you only see roles matching your flexibility needs.',
    tips: [
      'Remote, hybrid, and onsite have different implications',
      'Consider your commute tolerance realistically',
      'Some roles require specific locations regardless of preference'
    ]
  },
  compensation: {
    title: 'Compensation',
    purpose: 'Set your salary expectations and requirements.',
    why: 'We use this to filter out roles below your minimum, saving you time on unsuitable opportunities.',
    tips: [
      'Be realistic but don\'t undersell yourself',
      'Consider total compensation, not just base salary',
      'Your minimum is a hard filter — we won\'t show roles below it'
    ]
  },
  dealbreakers: {
    title: 'Deal Makers & Breakers',
    purpose: 'Identify non-negotiables that would make or break a role for you.',
    why: 'These are your hard filters. Deal breakers eliminate opportunities immediately; deal makers boost them in your rankings.',
    tips: [
      'Be selective — too many dealbreakers limits your options',
      'Deal makers are "nice to haves" that improve match scores',
      'Think about what made you leave previous roles'
    ]
  },
  disease_areas: {
    title: 'Disease Areas',
    purpose: 'Indicate your therapeutic area experience and interests.',
    why: 'Life sciences roles often require or prefer specific disease area expertise. This helps us match you with relevant opportunities.',
    tips: [
      'Include both experience and interest areas',
      'Therapeutic focus can differentiate you as a candidate',
      'Select broadly if you\'re open to learning new areas'
    ]
  },
  resume: {
    title: 'Resume',
    purpose: 'Upload your resume to complete your profile.',
    why: 'Your resume provides detailed context for our matching algorithm and helps verify your experience claims.',
    tips: [
      'Use your most current, polished resume',
      'PDF format is preferred for best compatibility',
      'Keep it under 10MB'
    ]
  }
};

// Section Help Modal Component
const SectionHelpModal = ({ isOpen, onClose, sectionId, onNavigateToHelp }) => {
  const help = SECTION_HELP[sectionId];
  if (!help) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-lime-600" />
            {help.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">What is this section?</h4>
            <p className="text-sm text-slate-600">{help.purpose}</p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">Why does it matter?</h4>
            <p className="text-sm text-slate-600">{help.why}</p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Tips</h4>
            <ul className="space-y-2">
              {help.tips.map((tip, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-4 w-4 text-lime-500 mt-0.5 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>
          <div className="pt-3 border-t border-slate-100">
            <button
              onClick={onNavigateToHelp}
              className="text-sm text-lime-600 hover:text-lime-700 font-medium flex items-center gap-1"
            >
              <BookOpen className="h-4 w-4" />
              View full Help Center
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Animation variants
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } }
};

// Card selection animation
const cardVariants = {
  initial: { scale: 1 },
  hover: { scale: 1.02, y: -2, transition: { duration: 0.2 } },
  tap: { scale: 0.98, transition: { duration: 0.1 } },
  selected: { 
    scale: 1,
    transition: { 
      type: "spring", 
      stiffness: 400, 
      damping: 25 
    }
  }
};

// Checkmark animation
const checkVariants = {
  initial: { scale: 0, opacity: 0 },
  animate: { 
    scale: 1, 
    opacity: 1, 
    transition: { 
      type: "spring", 
      stiffness: 500, 
      damping: 30,
      delay: 0.1
    }
  }
};

// Confetti particle component
const ConfettiParticle = ({ delay, color, startX }) => (
  <motion.div
    className="absolute w-3 h-3 rounded-sm"
    style={{ backgroundColor: color, left: `${startX}%` }}
    initial={{ y: 0, x: 0, opacity: 1, rotate: 0 }}
    animate={{ 
      y: [0, -100, 400], 
      x: [0, (Math.random() - 0.5) * 200],
      opacity: [1, 1, 0],
      rotate: [0, 360 + Math.random() * 360]
    }}
    transition={{ 
      duration: 2.5, 
      delay: delay,
      ease: [0.25, 0.46, 0.45, 0.94]
    }}
  />
);

// Completion Celebration Modal
const CompletionCelebration = ({ onComplete, referralUrl }) => {
  const confettiColors = ['#65a30d', '#84cc16', '#022c22', '#ecfccb', '#bef264'];
  const [copied, setCopied] = useState(false);
  
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl || 'https://varyynt.com');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  const shareLinkedIn = () => {
    const url = encodeURIComponent(referralUrl || 'https://varyynt.com');
    const text = encodeURIComponent("Just completed my career profile on Varyynt - an AI-powered platform that matches life sciences professionals with opportunities based on career aspirations. Highly recommend checking it out!");
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };
  
  return (
    <motion.div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-white"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Confetti */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(50)].map((_, i) => (
          <ConfettiParticle 
            key={i} 
            delay={i * 0.03} 
            color={confettiColors[i % confettiColors.length]}
            startX={Math.random() * 100}
          />
        ))}
      </div>
      
      {/* Content */}
      <motion.div 
        className="text-center px-8 relative z-10 max-w-lg"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
      >
        {/* Success icon */}
        <motion.div 
          className="w-24 h-24 mx-auto mb-8 rounded-full bg-lime-100 flex items-center justify-center"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.5, type: "spring", stiffness: 300 }}
          >
            <Check className="w-12 h-12 text-lime-600" strokeWidth={3} />
          </motion.div>
        </motion.div>
        
        <motion.h1 
          className="text-4xl md:text-5xl font-heading text-evergreen-950 mb-4"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          You're all set!
        </motion.h1>
        
        <motion.p 
          className="text-lg text-slate-500 mb-8 max-w-md mx-auto"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          Your profile is complete. We'll notify you when opportunities match your preferences.
        </motion.p>
        
        {/* Share Section */}
        <motion.div
          className="mb-8 p-6 bg-gradient-to-br from-lime-50 to-white border border-lime-200 rounded-2xl"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.55 }}
        >
          <div className="flex items-center justify-center gap-2 mb-3">
            <Share2 className="h-5 w-5 text-lime-600" />
            <span className="font-medium text-slate-900">Know colleagues exploring opportunities?</span>
          </div>
          <p className="text-sm text-slate-500 mb-4">Share Varyynt with your network</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button 
              onClick={shareLinkedIn}
              size="sm"
              className="bg-[#0A66C2] hover:bg-[#004182]"
            >
              <Linkedin className="h-4 w-4 mr-2" />
              Share on LinkedIn
            </Button>
            <Button 
              onClick={copyLink}
              size="sm"
              variant="outline"
            >
              {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
          </div>
        </motion.div>
        
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <Button
            onClick={onComplete}
            className="bg-evergreen-950 hover:bg-evergreen-900 text-white px-10 py-6 text-lg font-medium shadow-xl hover:shadow-2xl transition-all"
            data-testid="celebration-continue-btn"
          >
            Go to Dashboard
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

// Premium Multi-select component - Cellular Card Style with enhanced animations
const CellularMultiSelect = ({ options, selected, onChange, columns = 3, hasNoPreference = false }) => {
  const noPreferenceOption = options.find(o => o === 'No Preference');
  const regularOptions = options.filter(o => o !== 'No Preference' && o !== 'Other');
  const hasOther = options.includes('Other');
  const isNoPreferenceSelected = selected.includes('No Preference');

  const toggleOption = (option) => {
    if (option === 'No Preference') {
      onChange(selected.includes('No Preference') ? [] : ['No Preference']);
      return;
    }
    let newSelected = selected.filter(s => s !== 'No Preference');
    if (newSelected.includes(option)) {
      onChange(newSelected.filter(s => s !== option));
    } else {
      onChange([...newSelected, option]);
    }
  };

  return (
    <div className="space-y-6">
      {/* No Preference option */}
      {noPreferenceOption && (
        <div className="mb-6">
          <motion.button
            type="button"
            onClick={() => toggleOption('No Preference')}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            className={`w-full flex items-center gap-4 p-5 rounded-xl border-2 transition-all duration-300 group ${
              isNoPreferenceSelected
                ? 'border-evergreen-950 bg-evergreen-950 text-white shadow-lg'
                : 'border-slate-200 text-slate-600 hover:border-slate-400 hover:bg-slate-50 hover:shadow-md'
            }`}
            data-testid="option-no-preference"
          >
            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
              isNoPreferenceSelected ? 'bg-white border-white' : 'border-slate-300 group-hover:border-slate-400'
            }`}>
              <AnimatePresence>
                {isNoPreferenceSelected && (
                  <motion.div
                    variants={checkVariants}
                    initial="initial"
                    animate="animate"
                    exit="initial"
                  >
                    <Check className="h-4 w-4 text-evergreen-950" strokeWidth={3} />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <span className="font-medium tracking-wide">Open to all options</span>
          </motion.button>
          
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-4 text-sm text-slate-400">
                Or select specific preferences
              </span>
            </div>
          </div>
        </div>
      )}
      
      {/* Grid of options */}
      <div className={`grid gap-3 ${
        columns === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
      } ${isNoPreferenceSelected ? 'opacity-30 pointer-events-none' : ''}`}>
        {regularOptions.map((option, index) => {
          const isSelected = selected.includes(option);
          return (
            <motion.button
              key={option}
              type="button"
              onClick={() => toggleOption(option)}
              disabled={isNoPreferenceSelected}
              variants={cardVariants}
              initial="initial"
              whileHover="hover"
              whileTap="tap"
              animate={isSelected ? "selected" : "initial"}
              className={`relative flex items-center gap-4 p-5 rounded-xl border-2 text-left transition-colors duration-200 group overflow-hidden ${
                isSelected
                  ? 'border-lime-600 bg-lime-50 shadow-md shadow-lime-200/50'
                  : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50 hover:shadow-md'
              }`}
              data-testid={`option-${option.replace(/[\s\/]+/g, '-').toLowerCase()}`}
            >
              {/* Selection pulse effect */}
              <AnimatePresence>
                {isSelected && (
                  <motion.div
                    className="absolute inset-0 bg-lime-400/20 rounded-xl"
                    initial={{ scale: 0, opacity: 1 }}
                    animate={{ scale: 2, opacity: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.5 }}
                  />
                )}
              </AnimatePresence>
              
              <div className={`relative w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                isSelected 
                  ? 'bg-lime-600 border-lime-600' 
                  : 'border-slate-300 group-hover:border-slate-400'
              }`}>
                <AnimatePresence>
                  {isSelected && (
                    <motion.div
                      variants={checkVariants}
                      initial="initial"
                      animate="animate"
                      exit="initial"
                    >
                      <Check className="h-4 w-4 text-white" strokeWidth={3} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <span className={`font-medium text-sm ${isSelected ? 'text-evergreen-950' : 'text-slate-700'}`}>
                {option}
              </span>
            </motion.button>
          );
        })}
      </div>
      
      {/* "Other" option */}
      {hasOther && (
        <div className={`pt-4 border-t border-slate-100 ${isNoPreferenceSelected ? 'opacity-30 pointer-events-none' : ''}`}>
          <button
            type="button"
            onClick={() => toggleOption('Other')}
            disabled={isNoPreferenceSelected}
            className={`inline-flex items-center gap-3 px-5 py-3 rounded-xl border-2 transition-all duration-200 ${
              selected.includes('Other')
                ? 'border-lime-600 bg-lime-50'
                : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50'
            }`}
            data-testid="option-other"
          >
            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
              selected.includes('Other') ? 'bg-lime-600 border-lime-600' : 'border-slate-300'
            }`}>
              {selected.includes('Other') && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
            </div>
            <span className="font-medium text-sm text-slate-700">Other</span>
          </button>
        </div>
      )}
      
      {/* Selection count */}
      {selected.length > 0 && !isNoPreferenceSelected && (
        <p className="text-sm text-lime-700 font-medium">
          {selected.length} selected
        </p>
      )}
    </div>
  );
};

// Premium Single Select - Pill Style
const PillSelect = ({ options, selected, onChange }) => {
  const noPreferenceOption = options.find(o => o === 'No Preference' || o === 'No preference');
  const regularOptions = options.filter(o => o !== 'No Preference' && o !== 'No preference');
  
  return (
    <div className="space-y-4">
      {noPreferenceOption && (
        <div className="mb-6">
          <button
            type="button"
            onClick={() => onChange(noPreferenceOption)}
            className={`px-6 py-3 rounded-full border-2 font-medium transition-all duration-200 ${
              selected === noPreferenceOption
                ? 'bg-evergreen-950 border-evergreen-950 text-white'
                : 'border-slate-200 text-slate-600 hover:border-slate-400'
            }`}
            data-testid="option-no-preference"
          >
            Open to all
          </button>
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        {regularOptions.map((option) => {
          const isSelected = selected === option;
          return (
            <motion.button
              key={option}
              type="button"
              onClick={() => onChange(option)}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className={`px-6 py-3 rounded-full border-2 font-medium text-sm transition-all duration-200 ${
                isSelected
                  ? 'bg-evergreen-950 border-evergreen-950 text-white shadow-lg'
                  : 'border-slate-200 text-slate-700 hover:border-slate-400 hover:bg-slate-50'
              }`}
              data-testid={`option-${option.replace(/[\s\/]+/g, '-').toLowerCase()}`}
            >
              {option}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

// Premium Input with underlined style
const BioInput = ({ label, ...props }) => (
  <div className="space-y-2">
    {label && <label className="bio-label">{label}</label>}
    <Input 
      {...props} 
      className="bio-input bg-transparent border-0 border-b-2 border-slate-200 px-0 py-4 text-xl focus:border-evergreen-950 focus:ring-0 rounded-none transition-colors placeholder:text-slate-300 w-full" 
    />
  </div>
);

// Premium Textarea
const BioTextarea = ({ label, ...props }) => (
  <div className="space-y-2">
    {label && <label className="bio-label">{label}</label>}
    <Textarea 
      {...props} 
      className="bg-transparent border-2 border-slate-200 p-4 text-lg focus:border-evergreen-950 focus:ring-0 rounded-xl transition-colors placeholder:text-slate-400 min-h-[160px] resize-none" 
    />
  </div>
);

export default function OnboardingPage() {
  const { profile, preferences, saveOnboardingStep, authAxios, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const startSection = location.state?.startSection || 0;
  const [currentSection, setCurrentSection] = useState(startSection);
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [showSectionHelp, setShowSectionHelp] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    current_title: profile?.current_title || '',
    years_experience: profile?.years_experience || '',
    location_city: profile?.location_city || '',
    location_state: profile?.location_state || '',
    location_country: profile?.location_country || '',
    summary: profile?.summary || '',
    desired_industries: preferences?.desired_industries || [],
    desired_functions: preferences?.desired_functions || [],
    company_size_preferences: preferences?.company_size_preferences || [],
    company_stage_preferences: preferences?.company_stage_preferences || [],
    ownership_preferences: preferences?.ownership_preferences || [],
    work_model_preferences: preferences?.work_model_preferences || [],
    geography_preferences: preferences?.geography_preferences || [],
    base_salary_band: preferences?.base_salary_band || '',
    equity_preference: preferences?.equity_preference || '',
    raw_ideal_role: preferences?.raw_ideal_role || '',
    raw_dealbreakers: preferences?.raw_dealbreakers || '',
    disease_areas: [],
  });

  const [resumes, setResumes] = useState([]);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [referralUrl, setReferralUrl] = useState('');

  // Load referral URL for sharing
  useEffect(() => {
    const loadReferralInfo = async () => {
      try {
        const response = await authAxios.get('/candidates/referral/info');
        setReferralUrl(response.data.referral_url || 'https://varyynt.com');
      } catch (error) {
        console.error('Failed to load referral info:', error);
      }
    };
    loadReferralInfo();
  }, [authAxios]);

  useEffect(() => {
    const loadResumes = async () => {
      try {
        const response = await authAxios.get('/candidates/resumes');
        setResumes(response.data.resumes || []);
      } catch (error) {
        console.error('Failed to load resumes:', error);
      }
    };
    loadResumes();
  }, [authAxios]);

  useEffect(() => {
    const loadDiseaseAreas = async () => {
      try {
        const response = await authAxios.get('/candidates/disease-areas');
        setFormData(prev => ({
          ...prev,
          disease_areas: response.data.disease_areas || []
        }));
      } catch (error) {
        console.error('Failed to load disease areas:', error);
      }
    };
    loadDiseaseAreas();
  }, [authAxios]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const totalSteps = SECTIONS.reduce((acc, s) => acc + s.steps, 0);
  const completedSteps = SECTIONS.slice(0, currentSection).reduce((acc, s) => acc + s.steps, 0) + currentStep;
  const overallProgress = Math.round((completedSteps / totalSteps) * 100);

  const currentSectionData = SECTIONS[currentSection];
  const isLastStep = currentStep === currentSectionData.steps - 1;
  const isLastSection = currentSection === SECTIONS.length - 1;

  const saveCurrentStep = async (isComplete = false) => {
    setSaving(true);
    try {
      const section = currentSectionData.id;
      let data = {};

      switch (section) {
        case 'about_you':
          data = {
            current_title: formData.current_title,
            years_experience: formData.years_experience,
            location_city: formData.location_city,
            location_state: formData.location_state,
            location_country: formData.location_country,
            summary: formData.summary,
            is_complete: isComplete
          };
          break;
        case 'industry_function':
          data = {
            desired_industries: formData.desired_industries,
            desired_functions: formData.desired_functions,
            is_complete: isComplete
          };
          break;
        case 'company_preferences':
          data = {
            company_size_preferences: formData.company_size_preferences,
            company_stage_preferences: formData.company_stage_preferences,
            ownership_preferences: formData.ownership_preferences,
            is_complete: isComplete
          };
          break;
        case 'work_style':
          data = {
            work_model_preferences: formData.work_model_preferences,
            geography_preferences: formData.geography_preferences,
            is_complete: isComplete
          };
          break;
        case 'compensation':
          data = {
            base_salary_band: formData.base_salary_band,
            equity_preference: formData.equity_preference,
            is_complete: isComplete
          };
          break;
        case 'dealbreakers':
          data = {
            raw_ideal_role: formData.raw_ideal_role,
            raw_dealbreakers: formData.raw_dealbreakers,
            is_complete: isComplete
          };
          break;
        case 'disease_areas':
          data = {
            disease_areas: formData.disease_areas,
            is_complete: isComplete
          };
          break;
        case 'resume':
          data = {
            is_complete: resumes.length > 0
          };
          break;
        default:
          break;
      }

      await saveOnboardingStep(section, currentStep, data);
      return true;
    } catch (error) {
      toast.error('Failed to save. Please try again.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const isComplete = isLastStep;
    const saved = await saveCurrentStep(isComplete);
    if (!saved) return;

    if (isLastStep) {
      if (isLastSection) {
        // Show celebration instead of immediate navigation
        setShowCelebration(true);
      } else {
        setCurrentSection(prev => prev + 1);
        setCurrentStep(0);
      }
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleCelebrationComplete = () => {
    setShowCelebration(false);
    toast.success('Profile complete! Great job!');
    navigate('/dashboard');
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    } else if (currentSection > 0) {
      setCurrentSection(prev => prev - 1);
      setCurrentStep(SECTIONS[currentSection - 1].steps - 1);
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingResume(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('label', file.name);
    formDataUpload.append('is_primary', resumes.length === 0 ? 'true' : 'false');
    formDataUpload.append('auto_parse', 'true');  // Explicitly trigger AI parsing

    try {
      const uploadResponse = await authAxios.post('/candidates/resumes/upload', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const response = await authAxios.get('/candidates/resumes');
      setResumes(response.data.resumes || []);
      
      // Check if parsing succeeded and show appropriate message
      const resumeData = uploadResponse.data?.resume;
      if (resumeData?.parsed) {
        toast.success('Resume uploaded and analyzed successfully!');
      } else if (resumeData?.parse_error) {
        // Show specific error message
        toast.warning(resumeData.parse_error, { duration: 6000 });
      } else {
        toast.info('Resume uploaded! You can re-analyze it from your Profile page.');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to upload resume';
      toast.error(message);
    } finally {
      setUploadingResume(false);
    }
  };

  const handleDeleteResume = async (resumeId) => {
    try {
      await authAxios.delete(`/candidates/resumes/${resumeId}`);
      setResumes(prev => prev.filter(r => r.id !== resumeId));
      toast.success('Resume deleted');
    } catch (error) {
      toast.error('Failed to delete resume');
    }
  };

  const handleSetPrimaryResume = async (resumeId) => {
    try {
      await authAxios.put(`/candidates/resumes/${resumeId}/set-primary`);
      setResumes(prev => prev.map(r => ({
        ...r,
        is_primary: r.id === resumeId
      })));
      toast.success('Primary resume updated');
    } catch (error) {
      toast.error('Failed to update primary resume');
    }
  };

  // Render step content
  const renderStepContent = () => {
    const section = currentSectionData.id;

    // About You
    if (section === 'about_you') {
      if (currentStep === 0) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                What's your current role?
              </h2>
              <p className="text-lg text-slate-500 font-light">
                This helps us understand your experience level.
              </p>
            </div>
            <BioInput
              value={formData.current_title}
              onChange={(e) => updateFormData('current_title', e.target.value)}
              placeholder="e.g., Senior Clinical Research Associate"
              data-testid="input-current-title"
            />
          </div>
        );
      }
      if (currentStep === 1) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                Years of experience
              </h2>
              <p className="text-lg text-slate-500 font-light">
                Select the range that best describes your journey.
              </p>
            </div>
            <PillSelect
              options={EXPERIENCE_LEVELS}
              selected={formData.years_experience}
              onChange={(val) => updateFormData('years_experience', val)}
            />
          </div>
        );
      }
      if (currentStep === 2) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                Where are you based?
              </h2>
              <p className="text-lg text-slate-500 font-light">
                This helps us match you with local opportunities.
              </p>
            </div>
            <div className="space-y-6">
              <BioInput
                label="City"
                value={formData.location_city}
                onChange={(e) => updateFormData('location_city', e.target.value)}
                placeholder="Boston"
                data-testid="input-city"
              />
              <div className="grid grid-cols-2 gap-6">
                <BioInput
                  label="State / Region"
                  value={formData.location_state}
                  onChange={(e) => updateFormData('location_state', e.target.value)}
                  placeholder="Massachusetts"
                  data-testid="input-state"
                />
                <BioInput
                  label="Country"
                  value={formData.location_country}
                  onChange={(e) => updateFormData('location_country', e.target.value)}
                  placeholder="USA"
                  data-testid="input-country"
                />
              </div>
            </div>
          </div>
        );
      }
      if (currentStep === 3) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                Your career story
              </h2>
              <p className="text-lg text-slate-500 font-light">
                Optional: A few sentences about your professional journey.
              </p>
            </div>
            <BioTextarea
              value={formData.summary}
              onChange={(e) => updateFormData('summary', e.target.value)}
              placeholder="I have 8 years of experience in clinical development, focusing on oncology trials at major pharmaceutical companies..."
              data-testid="input-summary"
            />
          </div>
        );
      }
    }

    // Industries & Functions
    if (section === 'industry_function') {
      if (currentStep === 0) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                Which industries interest you?
              </h2>
              <p className="text-lg text-slate-500 font-light">
                Select all that apply.
              </p>
            </div>
            <CellularMultiSelect
              options={INDUSTRIES}
              selected={formData.desired_industries}
              onChange={(val) => updateFormData('desired_industries', val)}
              columns={2}
            />
          </div>
        );
      }
      if (currentStep === 1) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                What functions interest you?
              </h2>
              <p className="text-lg text-slate-500 font-light">
                Select all departments or roles you'd consider.
              </p>
            </div>
            <CellularMultiSelect
              options={FUNCTIONS}
              selected={formData.desired_functions}
              onChange={(val) => updateFormData('desired_functions', val)}
              columns={2}
            />
          </div>
        );
      }
    }

    // Company Preferences
    if (section === 'company_preferences') {
      if (currentStep === 0) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                Company size preferences
              </h2>
              <p className="text-lg text-slate-500 font-light">
                Select all sizes you're open to.
              </p>
            </div>
            <CellularMultiSelect
              options={COMPANY_SIZES}
              selected={formData.company_size_preferences}
              onChange={(val) => updateFormData('company_size_preferences', val)}
              columns={2}
            />
          </div>
        );
      }
      if (currentStep === 1) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                Development stage
              </h2>
              <p className="text-lg text-slate-500 font-light">
                Especially relevant for biotech roles.
              </p>
            </div>
            <CellularMultiSelect
              options={COMPANY_STAGES}
              selected={formData.company_stage_preferences}
              onChange={(val) => updateFormData('company_stage_preferences', val)}
              columns={2}
              hasNoPreference={true}
            />
          </div>
        );
      }
      if (currentStep === 2) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                Ownership structure
              </h2>
              <p className="text-lg text-slate-500 font-light">
                Do you have a preference?
              </p>
            </div>
            <CellularMultiSelect
              options={OWNERSHIP_TYPES}
              selected={formData.ownership_preferences}
              onChange={(val) => updateFormData('ownership_preferences', val)}
              columns={2}
            />
          </div>
        );
      }
    }

    // Work Style & Geography
    if (section === 'work_style') {
      if (currentStep === 0) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                Work arrangement
              </h2>
              <p className="text-lg text-slate-500 font-light">
                What work styles fit your life?
              </p>
            </div>
            <CellularMultiSelect
              options={WORK_MODELS}
              selected={formData.work_model_preferences}
              onChange={(val) => updateFormData('work_model_preferences', val)}
              columns={3}
            />
          </div>
        );
      }
      if (currentStep === 1) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                Geographic preferences
              </h2>
              <p className="text-lg text-slate-500 font-light">
                Where are you open to working?
              </p>
            </div>
            <CellularMultiSelect
              options={GEOGRAPHIES}
              selected={formData.geography_preferences}
              onChange={(val) => updateFormData('geography_preferences', val)}
              columns={3}
            />
          </div>
        );
      }
    }

    // Compensation
    if (section === 'compensation') {
      if (currentStep === 0) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                Target compensation
              </h2>
              <p className="text-lg text-slate-500 font-light">
                Non-binding preferences to help with matching.
              </p>
            </div>
            <PillSelect
              options={SALARY_BANDS}
              selected={formData.base_salary_band}
              onChange={(val) => updateFormData('base_salary_band', val)}
            />
          </div>
        );
      }
      if (currentStep === 1) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                Equity preference
              </h2>
              <p className="text-lg text-slate-500 font-light">
                How do you weigh equity vs cash?
              </p>
            </div>
            <PillSelect
              options={EQUITY_PREFERENCES}
              selected={formData.equity_preference}
              onChange={(val) => updateFormData('equity_preference', val)}
            />
          </div>
        );
      }
    }

    // Dealbreakers
    if (section === 'dealbreakers') {
      if (currentStep === 0) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                Your ideal role
              </h2>
              <p className="text-lg text-slate-500 font-light">
                What truly matters to you? Think culture, mission, autonomy, growth.
              </p>
            </div>
            <BioTextarea
              value={formData.raw_ideal_role}
              onChange={(e) => updateFormData('raw_ideal_role', e.target.value)}
              placeholder="I'm looking for a role where I can lead clinical strategy, have autonomy in decision-making, work with a mission-driven team focused on rare diseases..."
              data-testid="input-ideal-role"
            />
          </div>
        );
      }
      if (currentStep === 1) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                Your dealbreakers
              </h2>
              <p className="text-lg text-slate-500 font-light">
                What do you want to avoid?
              </p>
            </div>
            <BioTextarea
              value={formData.raw_dealbreakers}
              onChange={(e) => updateFormData('raw_dealbreakers', e.target.value)}
              placeholder="I want to avoid roles with excessive travel (>50%), companies with toxic cultures, or positions that are purely administrative..."
              data-testid="input-dealbreakers"
            />
          </div>
        );
      }
    }

    // Disease Areas
    if (section === 'disease_areas') {
      if (currentStep === 0) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                Disease area expertise
              </h2>
              <p className="text-lg text-slate-500 font-light">
                Select areas relevant to your background or interests.
              </p>
            </div>
            <CellularMultiSelect
              options={DISEASE_AREAS}
              selected={formData.disease_areas.map(d => d.disease_area)}
              onChange={(selected) => {
                const updated = selected.map(area => {
                  const existing = formData.disease_areas.find(d => d.disease_area === area);
                  return existing || { disease_area: area, experience_level: 'Interest only' };
                });
                updateFormData('disease_areas', updated);
              }}
              columns={3}
            />
          </div>
        );
      }
      if (currentStep === 1 && formData.disease_areas.length > 0) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                Rate your experience
              </h2>
              <p className="text-lg text-slate-500 font-light">
                Help us understand your depth of expertise.
              </p>
            </div>
            <div className="space-y-6">
              {formData.disease_areas.map((area, index) => (
                <div key={area.disease_area} className="p-6 bg-slate-50 rounded-xl border border-slate-100">
                  <p className="font-semibold text-slate-900 mb-4">{area.disease_area}</p>
                  <div className="flex flex-wrap gap-2">
                    {DISEASE_EXP_LEVELS.map(level => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => {
                          const updated = [...formData.disease_areas];
                          updated[index].experience_level = level;
                          updateFormData('disease_areas', updated);
                        }}
                        className={`px-4 py-2 rounded-full text-sm transition-all ${
                          area.experience_level === level
                            ? 'bg-evergreen-950 text-white'
                            : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-400'
                        }`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      }
      if (currentStep === 1 && formData.disease_areas.length === 0) {
        return (
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
                No areas selected
              </h2>
              <p className="text-lg text-slate-500 font-light">
                That's perfectly fine. Click continue to proceed.
              </p>
            </div>
          </div>
        );
      }
    }

    // Resume
    if (section === 'resume') {
      return (
        <div className="space-y-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-heading tracking-tight text-slate-900 mb-3">
              Upload your resume
            </h2>
            <p className="text-lg text-slate-500 font-light">
              PDF or DOCX format, up to 10MB.
            </p>
          </div>
          
          {/* Upload area */}
          <div className="relative group">
            <input
              type="file"
              accept=".pdf,.docx,.doc"
              onChange={handleResumeUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              disabled={uploadingResume}
              data-testid="resume-upload-input"
            />
            <div className="border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center transition-all duration-300 group-hover:border-lime-400 group-hover:bg-lime-50/50">
              {uploadingResume ? (
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="h-12 w-12 text-lime-600 animate-spin" />
                  <p className="text-slate-600">Uploading...</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-4">
                  <div className="h-16 w-16 rounded-full bg-lime-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Upload className="h-8 w-8 text-lime-600" />
                  </div>
                  <div>
                    <p className="text-slate-900 font-semibold text-lg">
                      Drop your resume here
                    </p>
                    <p className="text-sm text-slate-500 mt-1">or click to browse</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Resume list */}
          {resumes.length > 0 && (
            <div className="space-y-3">
              <p className="bio-label">Your Resumes</p>
              {resumes.map((resume) => (
                <div 
                  key={resume.id} 
                  className="flex items-center justify-between p-5 bg-slate-50 rounded-xl border border-slate-100"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{resume.label}</p>
                      <p className="text-sm text-slate-500">
                        {resume.is_primary && (
                          <span className="text-lime-600 font-medium">Primary • </span>
                        )}
                        Uploaded {new Date(resume.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!resume.is_primary && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetPrimaryResume(resume.id)}
                        className="text-slate-600 hover:text-evergreen-950"
                      >
                        Set Primary
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteResume(resume.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  const CurrentIcon = currentSectionData.icon;

  // Show celebration modal if complete
  if (showCelebration) {
    return <CompletionCelebration onComplete={handleCelebrationComplete} referralUrl={referralUrl} />;
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Split screen layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-screen">
        
        {/* Left Sidebar - Progress Tracker */}
        <aside className="hidden lg:flex lg:col-span-4 wizard-sidebar border-r border-slate-100 p-8 flex-col justify-between sticky top-0 h-screen">
          <div>
            {/* Logo */}
            <Link to="/dashboard" className="inline-block mb-12">
              <span className="text-2xl font-heading text-evergreen-950">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </span>
            </Link>
            
            {/* Section progress - DNA-style timeline */}
            <nav className="space-y-0">
              {SECTIONS.map((section, index) => {
                const isComplete = index < currentSection;
                const isCurrent = index === currentSection;
                const isUpcoming = index > currentSection;
                const Icon = section.icon;
                
                return (
                  <div key={section.id} className="relative">
                    <div className={`flex items-center gap-4 py-3 ${isCurrent ? '' : ''}`}>
                      {/* Dot */}
                      <div className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isComplete 
                          ? 'bg-lime-600 text-white' 
                          : isCurrent 
                            ? 'bg-evergreen-950 text-white ring-4 ring-lime-100' 
                            : 'bg-white border-2 border-slate-200 text-slate-400'
                      }`}>
                        {isComplete ? (
                          <Check className="h-5 w-5" strokeWidth={2.5} />
                        ) : (
                          <Icon className="h-5 w-5" strokeWidth={1.5} />
                        )}
                      </div>
                      
                      {/* Label */}
                      <div>
                        <p className={`font-medium text-sm transition-colors ${
                          isCurrent ? 'text-evergreen-950' : isComplete ? 'text-slate-700' : 'text-slate-400'
                        }`}>
                          {section.name}
                        </p>
                        {isCurrent && (
                          <p className="text-xs text-lime-600 font-medium mt-0.5">
                            Step {currentStep + 1} of {section.steps}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Connecting line */}
                    {index < SECTIONS.length - 1 && (
                      <div className={`absolute left-5 top-[52px] w-0.5 h-4 transition-colors ${
                        isComplete ? 'bg-lime-600' : 'bg-slate-200'
                      }`} />
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
          
          {/* Progress percentage */}
          <div className="mt-auto pt-8 border-t border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-400">Progress</span>
              <span className="text-2xl font-heading text-evergreen-950">{overallProgress}%</span>
            </div>
            <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-lime-600 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${overallProgress}%` }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </div>
          </div>
        </aside>
        
        {/* Right Main Content */}
        <main className="lg:col-span-8 flex flex-col min-h-screen">
          {/* Mobile header */}
          <header className="lg:hidden sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-100 px-4 py-4">
            <div className="flex items-center justify-between mb-3">
              <Link to="/dashboard" className="text-xl font-heading text-evergreen-950">
                <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
              </Link>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/dashboard')}
                className="text-slate-500"
                data-testid="exit-onboarding-btn-mobile"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-lime-600 rounded-full transition-all duration-300"
                  style={{ width: `${overallProgress}%` }}
                />
              </div>
              <span className="text-sm font-medium text-evergreen-950">{overallProgress}%</span>
            </div>
          </header>
          
          {/* Content area */}
          <div className="flex-1 flex flex-col justify-center p-6 md:p-12 lg:p-20">
            <div className="max-w-2xl w-full mx-auto">
              {/* Section label - desktop */}
              <div className="hidden lg:flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg bg-lime-100 flex items-center justify-center">
                    <CurrentIcon className="h-4 w-4 text-lime-700" strokeWidth={1.5} />
                  </div>
                  <span className="text-sm font-medium text-slate-400">
                    {currentSectionData.name}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowSectionHelp(true)}
                  className="text-slate-400 hover:text-lime-600 hover:bg-lime-50"
                  data-testid="section-help-btn"
                >
                  <HelpCircle className="h-4 w-4 mr-1.5" />
                  <span className="text-xs">Help</span>
                </Button>
              </div>
              
              {/* Animated content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={`${currentSection}-${currentStep}`}
                  variants={pageVariants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                >
                  {renderStepContent()}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
          
          {/* Footer navigation */}
          <footer className="sticky bottom-0 bg-white/90 backdrop-blur-sm border-t border-slate-100 px-6 md:px-12 lg:px-20 py-4">
            <div className="max-w-2xl mx-auto flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={currentSection === 0 && currentStep === 0}
                className="text-slate-500 hover:text-evergreen-950"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              
              <div className="hidden lg:block">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/dashboard')}
                  className="text-slate-400 hover:text-slate-600"
                  data-testid="exit-onboarding-btn"
                >
                  Save & Exit
                </Button>
              </div>
              
              <Button
                onClick={handleNext}
                disabled={saving}
                className="bg-evergreen-950 hover:bg-evergreen-900 text-white px-8 py-6 text-base font-medium shadow-lg hover:shadow-xl transition-all"
                data-testid="next-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : isLastSection && isLastStep ? (
                  <>
                    Complete Profile
                    <Check className="h-4 w-4 ml-2" />
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </footer>
        </main>
      </div>
      
      {/* Section Help Modal */}
      <SectionHelpModal
        isOpen={showSectionHelp}
        onClose={() => setShowSectionHelp(false)}
        sectionId={currentSectionData.id}
        onNavigateToHelp={() => {
          setShowSectionHelp(false);
          navigate('/help');
        }}
      />
    </div>
  );
}
